import * as React from 'react';
import { Plus } from 'lucide-react';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import {
  errorMessage,
  useAddDomain,
  useAddPartner,
  useCreateProduct,
  useDecideDomain,
  type KbDomain,
  type KbVendor,
} from '@/lib/api/vendor-knowledge';
import { DChip, DChipAdd, FormError, Linkish, smallControl } from './kb-bits';
import { PLATFORM_TYPES, platformLabel, possessive } from './kb-logic';
import { useKb } from './vendor-knowledge-screen';

/**
 * Catalog edits a curator makes in place on a vendor's header (T-0206;
 * E13-F01 §3, architect's decision "adding catalog entries by hand"): a
 * web domain, a product, an implementation partner, and the approval of
 * domains proposed by the Caliber v1 import. Each opens a small form in
 * the row it changes; nothing opens over the page.
 */

/** "example.com" from what a curator typed or pasted. The server checks it properly. */
export function cleanHost(typed: string): string {
  return typed
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/[/?#].*$/, '');
}

function useOneOpen() {
  const [open, setOpen] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  return {
    open,
    error,
    setError,
    start: () => {
      setError(null);
      setOpen(true);
    },
    stop: () => {
      setError(null);
      setOpen(false);
    },
  };
}

export function DomainsFact({ data, fresh }: { data: KbVendor; fresh: boolean }) {
  const { canCurate } = useKb();
  const { toast } = useToast();
  const form = useOneOpen();
  const [text, setText] = React.useState('');
  const add = useAddDomain();
  const active = data.domains.filter((d) => d.status === 'active');
  const proposed = canCurate ? data.domains.filter((d) => d.status === 'proposed') : [];

  const submit = () => {
    const host = cleanHost(text);
    if (!host) {
      form.setError('Enter a domain such as example.com.');
      return;
    }
    add.mutate(
      { vendorId: data.vendor.id, host },
      {
        onSuccess: () => {
          toast({ message: `Added ${host}. Pages on it are captured without extra approval.` });
          setText('');
          form.stop();
        },
        onError: (err) => form.setError(errorMessage(err)),
      },
    );
  };

  return (
    <>
      {active.map((d) => (
        <DChip key={d.id}>{d.host}</DChip>
      ))}
      {proposed.map((d) => (
        <ProposedDomain key={d.id} domain={d} />
      ))}
      {active.length === 0 && proposed.length === 0 && !form.open ? (
        <span className="text-meta text-ink-500">
          {fresh && canCurate ? 'None yet. Add one in the first step below.' : 'None yet'}
        </span>
      ) : null}
      {canCurate && form.open ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <input
            autoFocus
            className={`${smallControl} w-[200px]`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') form.stop();
            }}
            placeholder="example.com"
            aria-label="Web domain"
            autoComplete="off"
          />
          <WorkspaceButton onClick={submit} disabled={add.isPending}>
            Add
          </WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={form.stop}>
            Cancel
          </WorkspaceButton>
          {form.error ? <FormError>{form.error}</FormError> : null}
        </span>
      ) : canCurate && !(fresh && active.length === 0) ? (
        <DChipAdd onClick={form.start}>
          <Plus className="h-2.5 w-2.5" aria-hidden="true" /> Add domain
        </DChipAdd>
      ) : null}
    </>
  );
}

/** A domain the Caliber v1 import proposed: its pages wait until a curator approves it. */
function ProposedDomain({ domain }: { domain: KbDomain }) {
  const { toast } = useToast();
  const decide = useDecideDomain();
  const [rejecting, setRejecting] = React.useState(false);
  const [why, setWhy] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const run = (verb: 'approve' | 'reject') =>
    decide.mutate(
      { domainId: domain.id, verb, reason: why.trim() },
      {
        onSuccess: (out) => {
          const activated =
            typeof out['activated_sources'] === 'number' ? out['activated_sources'] : 0;
          toast({
            message:
              verb === 'approve'
                ? `Approved ${domain.host}.${activated > 0 ? ` ${activated} ${activated === 1 ? 'page is' : 'pages are'} checked next.` : ''}`
                : `Rejected ${domain.host}. Its pages are not fetched.`,
          });
          setRejecting(false);
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-meta ${rejecting ? 'flex-wrap' : 'flex-nowrap whitespace-nowrap'}`}
    >
      <span
        title="Proposed by the Caliber v1 import. Its pages wait until it is approved."
        className="inline-flex h-[22px] items-center rounded-full border border-dashed border-accent-amber-500 bg-accent-amber-50 px-2 font-medium text-ink-900"
      >
        {domain.host}
      </span>
      <span className="text-ink-500">proposed</span>
      {rejecting ? (
        <>
          <input
            autoFocus
            className={`${smallControl} h-[26px] w-[180px]`}
            value={why}
            onChange={(e) => setWhy(e.target.value)}
            placeholder="Why reject it?"
            aria-label={`Why reject ${domain.host}?`}
            autoComplete="off"
          />
          <WorkspaceButton
            variant="destructive"
            className="h-[26px]"
            disabled={!why.trim() || decide.isPending}
            onClick={() => run('reject')}
          >
            Reject
          </WorkspaceButton>
          <WorkspaceButton
            variant="tertiary"
            className="h-[26px]"
            onClick={() => setRejecting(false)}
          >
            Cancel
          </WorkspaceButton>
        </>
      ) : (
        <>
          <Linkish disabled={decide.isPending} onClick={() => run('approve')}>
            Approve
          </Linkish>
          <Linkish tone="quiet" onClick={() => setRejecting(true)}>
            Reject
          </Linkish>
        </>
      )}
      {error ? <FormError>{error}</FormError> : null}
    </span>
  );
}

export function ProductsFact({ data, fresh }: { data: KbVendor; fresh: boolean }) {
  const { canCurate } = useKb();
  const { toast } = useToast();
  const form = useOneOpen();
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState(data.vendor.platform_type ?? 'erp');
  const create = useCreateProduct();
  const products = data.products.filter((p) => p.status === 'active');

  const submit = () => {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (clean.length < 2) {
      form.setError('Type the product’s name.');
      return;
    }
    create.mutate(
      { vendorId: data.vendor.id, name: clean, platform_type: type },
      {
        onSuccess: () => {
          toast({ message: `Added ${clean}.` });
          setName('');
          form.stop();
        },
        onError: (err) => form.setError(errorMessage(err)),
      },
    );
  };

  return (
    <>
      {products.map((p) => (
        <DChip key={p.id} title={platformLabel(p.platform_type)}>
          {p.name}
        </DChip>
      ))}
      {products.length === 0 && !form.open ? (
        <span className="text-meta text-ink-500">
          {fresh && canCurate ? 'None yet. Add one in the steps below.' : 'None yet'}
        </span>
      ) : null}
      {canCurate && form.open ? (
        <ProductForm
          autoFocus
          name={name}
          setName={setName}
          type={type}
          setType={setType}
          onSubmit={submit}
          onCancel={form.stop}
          pending={create.isPending}
          error={form.error}
        />
      ) : canCurate && !(fresh && products.length === 0) ? (
        <DChipAdd onClick={form.start}>
          <Plus className="h-2.5 w-2.5" aria-hidden="true" /> Add product
        </DChipAdd>
      ) : null}
    </>
  );
}

export function ProductForm({
  name,
  setName,
  type,
  setType,
  onSubmit,
  onCancel,
  pending,
  error,
  autoFocus = false,
}: {
  autoFocus?: boolean;
  name: string;
  setName: (v: string) => void;
  type: string;
  setType: (v: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  pending: boolean;
  error: string | null;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <input
        autoFocus={autoFocus}
        className={`${smallControl} w-[200px]`}
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onSubmit();
          if (e.key === 'Escape') onCancel?.();
        }}
        placeholder="Product name"
        aria-label="Product name"
        autoComplete="off"
      />
      <select
        className={`${smallControl} pr-7`}
        value={type}
        onChange={(e) => setType(e.target.value)}
        aria-label="Platform type"
      >
        {PLATFORM_TYPES.map((t) => (
          <option key={t.id} value={t.id}>
            {t.label}
          </option>
        ))}
      </select>
      <WorkspaceButton onClick={onSubmit} disabled={pending}>
        Add
      </WorkspaceButton>
      {onCancel ? (
        <WorkspaceButton variant="tertiary" onClick={onCancel}>
          Cancel
        </WorkspaceButton>
      ) : null}
      {error ? <FormError>{error}</FormError> : null}
    </span>
  );
}

const NEW_PARTNER = '@new';

export function PartnersFact({ data }: { data: KbVendor }) {
  const { canCurate, tree } = useKb();
  const { toast } = useToast();
  const form = useOneOpen();
  const [pick, setPick] = React.useState('');
  const [name, setName] = React.useState('');
  const add = useAddPartner();
  const linked = new Set(data.partners.map((p) => p.id));
  const others = tree.partners.filter((p) => p.status === 'active' && !linked.has(p.id));
  const productIds = data.products.filter((p) => p.status === 'active').map((p) => p.id);

  const submit = () => {
    if (!pick) return;
    const existing = pick === NEW_PARTNER ? null : tree.partners.find((p) => p.id === pick);
    const clean = name.trim().replace(/\s+/g, ' ');
    if (!existing && clean.length < 2) {
      form.setError('Type the partner’s name.');
      return;
    }
    if (productIds.length === 0) {
      form.setError(`Add a product first: a partner is linked to the products it implements.`);
      return;
    }
    add.mutate(
      { partnerId: existing?.id ?? null, name: clean, productIds },
      {
        onSuccess: () => {
          toast({
            message: `${existing?.name ?? clean} is now an implementation partner for ${data.vendor.name}.`,
          });
          setPick('');
          setName('');
          form.stop();
        },
        onError: (err) => form.setError(errorMessage(err)),
      },
    );
  };

  return (
    <>
      {data.partners.map((p) => (
        <DChip key={p.id}>{p.name}</DChip>
      ))}
      {data.partners.length === 0 && !form.open ? (
        <span className="text-meta text-ink-500">None recorded</span>
      ) : null}
      {canCurate && form.open ? (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <select
            autoFocus
            className={`${smallControl} pr-7`}
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            aria-label="Partner"
          >
            <option value="" disabled>
              Pick a partner
            </option>
            {others.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
            <option value={NEW_PARTNER}>A new partner…</option>
          </select>
          {pick === NEW_PARTNER ? (
            <input
              autoFocus
              className={`${smallControl} w-[200px]`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
              placeholder="Partner name"
              aria-label="New partner name"
              autoComplete="off"
            />
          ) : null}
          <WorkspaceButton onClick={submit} disabled={!pick || add.isPending}>
            Add
          </WorkspaceButton>
          <WorkspaceButton variant="tertiary" onClick={form.stop}>
            Cancel
          </WorkspaceButton>
          {form.error ? <FormError>{form.error}</FormError> : null}
        </span>
      ) : canCurate ? (
        <DChipAdd onClick={form.start}>
          <Plus className="h-2.5 w-2.5" aria-hidden="true" /> Add partner
        </DChipAdd>
      ) : null}
    </>
  );
}

/** The first step of a new vendor's guide: its first web domain, typed in place. */
export function DomainStepForm({ data }: { data: KbVendor }) {
  const { toast } = useToast();
  const [text, setText] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const add = useAddDomain();
  const submit = () => {
    const host = cleanHost(text);
    if (!host) {
      setError('Enter a domain such as example.com.');
      return;
    }
    add.mutate(
      { vendorId: data.vendor.id, host },
      {
        onSuccess: () => {
          toast({ message: `Added ${host}. Pages on it are captured without extra approval.` });
          setText('');
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <input
        className={`${smallControl} w-[200px]`}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
        }}
        placeholder="example.com"
        aria-label={`${possessive(data.vendor.name)} web domain`}
        autoComplete="off"
      />
      <WorkspaceButton onClick={submit} disabled={add.isPending}>
        Add
      </WorkspaceButton>
      {error ? <FormError>{error}</FormError> : null}
    </span>
  );
}

/** The second step: its first product. */
export function ProductStepForm({ data }: { data: KbVendor }) {
  const { toast } = useToast();
  const [name, setName] = React.useState('');
  const [type, setType] = React.useState(data.vendor.platform_type ?? 'erp');
  const [error, setError] = React.useState<string | null>(null);
  const create = useCreateProduct();
  const submit = () => {
    const clean = name.trim().replace(/\s+/g, ' ');
    if (clean.length < 2) {
      setError('Type the product’s name.');
      return;
    }
    create.mutate(
      { vendorId: data.vendor.id, name: clean, platform_type: type },
      {
        onSuccess: () => {
          toast({ message: `Added ${clean}.` });
          setName('');
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };
  return (
    <ProductForm
      name={name}
      setName={(v) => {
        setName(v);
        setError(null);
      }}
      type={type}
      setType={setType}
      onSubmit={submit}
      pending={create.isPending}
      error={error}
    />
  );
}
